import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';

const SellersToolbox = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState({
    title: 'Sellers Toolbox',
    subtitle: 'Comprehensive seller empowerment platform providing manufacturers and content creators with essential tools, resources, and guidance for successful 3D model monetization and marketplace optimization.',
    cta_title: 'Ready to elevate your business?',
    cta_subtitle: 'Join the thousands of manufacturers worldwide who are already using OpenGadget to streamline their localized 3D production.'
  });

  const getPageIcon = (page) => {
    switch (page) {
      case 'seller-toolbox': return 'Store';
      case 'documentation': return 'BookOpen';
      case 'api-reference': return 'Code';
      case 'tutorials': return 'Video';
      case 'resources-hub': return 'Globe';
      default: return 'FileText';
    }
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [posts, cats, settings] = await Promise.all([
          blogService.getPublishedPosts({ targetPage: 'seller-toolbox', limit: 12 }),
          blogService.getCategories(),
          blogService.getPageSettings('seller-toolbox')
        ]);
        setBlogPosts(posts);
        setCategories(cats || []);
        if (settings) setPageSettings(settings);
      } catch (error) {
        console.error('Error fetching sellers toolbox data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, []);
  
  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category?.id === activeCategory);

  const toolboxSections = [
    {
      id: 'upload-guidelines',
      title: 'Model Upload Guidelines',
      icon: 'Upload',
      description: 'Technical specifications and quality standards for optimal model performance',
      features: [
        {
          title: 'Technical Requirements',
          items: [
            'File formats: GLTF, GLB, OBJ, FBX (up to 100MB)',
            'Polygon count: 5K-100K for optimal performance',
            'Texture resolution: 1K-4K (power of 2)',
            'Material standards: PBR workflow recommended'
          ]
        },
        {
          title: 'Quality Standards',
          items: [
            'Clean topology with proper edge flow',
            'UV unwrapping without overlaps',
            'Optimized texture maps (diffuse, normal, roughness, metallic)',
            'Proper scale and orientation (real-world units)'
          ]
        },
        {
          title: 'Optimization Best Practices',
          items: [
            'LOD (Level of Detail) models for performance',
            'Compressed textures for faster loading',
            'Efficient material usage and shader optimization',
            'Metadata tags for better discoverability'
          ]
        }
      ]
    },
    {
      id: 'business-dev',
      title: 'Business Development Center',
      icon: 'TrendingUp',
      description: 'Strategic pricing, market analysis, and positioning guidance',
      features: [
        {
          title: 'Pricing Strategies',
          items: [
            'Competitive pricing analysis tools',
            'Value-based pricing calculator',
            'Bundle and volume discount setup',
            'Dynamic pricing recommendations'
          ]
        },
        {
          title: 'Market Analysis',
          items: [
            'Trending categories and search terms',
            'Competitor analysis dashboard',
            'Seasonal demand forecasting',
            'Customer demographic insights'
          ]
        },
        {
          title: 'Portfolio Optimization',
          items: [
            'Product performance scoring',
            'Gap analysis in your catalog',
            'Cross-selling opportunities',
            'Inventory optimization recommendations'
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Revenue Analytics Dashboard',
      icon: 'BarChart3',
      description: 'Detailed sales metrics, customer insights, and performance tracking',
      features: [
        {
          title: 'Sales Performance',
          items: [
            'Real-time revenue tracking',
            'Product-level sales analysis',
            'Conversion rate optimization',
            'Revenue trends and forecasting'
          ]
        },
        {
          title: 'Customer Insights',
          items: [
            'Customer acquisition costs',
            'Lifetime value calculations',
            'Purchase behavior analysis',
            'Customer satisfaction metrics'
          ]
        },
        {
          title: 'Actionable Recommendations',
          items: [
            'Underperforming product alerts',
            'Pricing optimization suggestions',
            'Marketing campaign effectiveness',
            'Inventory management insights'
          ]
        }
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing Resources',
      icon: 'Megaphone',
      description: 'Promotional tools, templates, and campaign management',
      features: [
        {
          title: 'Promotional Assets',
          items: [
            'Product showcase templates',
            'Social media post generators',
            'Email marketing templates',
            'Banner and thumbnail creators'
          ]
        },
        {
          title: 'Campaign Management',
          items: [
            'Multi-channel campaign builder',
            'A/B testing framework',
            'Campaign performance tracking',
            'ROI analytics dashboard'
          ]
        },
        {
          title: 'Content Strategy',
          items: [
            'SEO optimization guides',
            'Product description templates',
            'Keyword research tools',
            'Content calendar planning'
          ]
        }
      ]
    }
  ];

  const additionalResources = [
    {
      title: 'Legal & Licensing Hub',
      icon: 'Scale',
      description: 'Intellectual property protection, licensing options, and contract templates',
      links: ['License Agreement Templates', 'IP Protection Guide', 'Commercial Use Terms', 'Royalty Management']
    },
    {
      title: 'Quality Assurance Toolkit',
      icon: 'CheckCircle',
      description: 'Automated validation, compliance checklists, and submission workflows',
      links: ['Model Validator', 'Compliance Checklist', 'Submission Workflow', 'Quality Score Calculator']
    },
    {
      title: 'Seller Community Forum',
      icon: 'Users',
      description: 'Peer networking, knowledge sharing, and collaborative problem-solving',
      links: ['Discussion Forums', 'Success Stories', 'Expert Advice', 'Networking Events']
    },
    {
      title: 'Educational Content',
      icon: 'GraduationCap',
      description: 'Video tutorials, webinars, and case studies for successful selling',
      links: ['Video Tutorials', 'Live Webinars', 'Case Studies', 'Best Practices Guide']
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section - DARK CONTRASTING BACKGROUND */}
      <div className="bg-slate-900 text-white py-24 border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-2xl mb-6 shadow-lg shadow-blue-500/20">
              <Icon name="Store" size={40} className="text-white" />
            </div>
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight">{pageSettings.title}</h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed font-medium">
              {pageSettings.subtitle}
            </p>
            <div className="flex justify-center gap-4 mb-12">
              <Button 
                variant="default" 
                className="bg-blue-600 hover:bg-blue-700 text-white border-none px-8 py-3"
                onClick={() => navigate('/seller-signup')}
              >
                Get Started
              </Button>
              <Button 
                variant="outline" 
                className="border-slate-700 text-white hover:bg-slate-800 px-8 py-3"
                onClick={() => navigate('/seller-signup')}
              >
                Watch Demo
              </Button>
            </div>

            <ResourceSearch placeholder="Search seller resources, guides, tips..." />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Blog / Resource Center Section */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 text-left">
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Seller Resource Center</h2>
              <p className="text-slate-600 mt-2 text-lg">Insights, guides, and strategies to grow your business from traditional parts sourcing and including on demoand 3D manufacturing, even if you know nothing about 3D</p>
            </div>
            <Link to="/resources-hub" className="text-blue-600 hover:text-blue-700 font-bold flex items-center group bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 transition-all hover:shadow-md">
              View All Articles
              <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Category Filter Chips */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                  activeCategory === 'all' 
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                }`}
              >
                All Topics
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                    activeCategory === cat.id 
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                    : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl h-80 shadow-sm border border-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 text-left">
              {filteredPosts?.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                  className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                >
                  <div className="h-48 bg-slate-100 overflow-hidden relative">
                    {post.featured_image ? (
                      <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <Icon name={getPageIcon(post.targetPage)} size={48} />
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-sm">
                        {post.category?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center text-blue-600 font-semibold text-sm">
                      Read More
                      <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
              {filteredPosts?.length === 0 && (
                <div className="col-span-3 py-12 text-center text-slate-500 bg-white rounded-2xl border border-dashed border-slate-200">
                  No articles found in this category.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Core Toolbox Sections */}
        <div className="grid md:grid-cols-2 gap-12 mb-16 text-left">
          {toolboxSections?.map((section, index) => (
            <div key={index} id={section?.id} className="scroll-mt-24">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                <div className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="p-3 bg-blue-50 rounded-xl text-blue-600 font-bold">
                      <Icon name={section?.icon} size={24} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-800">{section?.title}</h3>
                      <p className="text-slate-500 text-sm mt-1">{section?.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-1 gap-8">
                    {section?.features?.map((feature, idx) => (
                      <div key={idx} className="space-y-3">
                        <h4 className="text-lg font-bold text-slate-800 flex items-center">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2"></span>
                          {feature?.title}
                        </h4>
                        <ul className="space-y-2">
                          {feature?.items?.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start text-slate-600 text-sm">
                              <Icon name="Check" size={14} className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-slate-50 px-8 py-4 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Enterprise Ready</span>
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 p-0 font-bold">
                    Access Resource <Icon name="ChevronRight" size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Resources Section */}
        <div className="bg-slate-900 rounded-3xl p-12 text-white relative overflow-hidden shadow-2xl text-left">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-10 text-center">Additional Support & Legal</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {additionalResources?.map((resource, index) => (
                <div key={index} className="space-y-4">
                  <div className="flex items-center space-x-3 text-blue-400">
                    <Icon name={resource?.icon} size={20} />
                    <h3 className="font-bold text-white">{resource?.title}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">{resource?.description}</p>
                  <ul className="space-y-2">
                    {resource?.links?.map((link, idx) => (
                      <li key={idx}>
                        <a href="#" className="text-xs text-slate-500 hover:text-blue-400 transition-colors flex items-center">
                          <Icon name="ArrowRight" size={10} className="mr-2" />
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA - DYNAMIC FROM DATABASE */}
        <div className="mt-20 text-center bg-white rounded-3xl p-12 shadow-xl border border-slate-100">
          <h2 className="text-4xl font-extrabold text-slate-800 mb-4">{pageSettings.cta_title}</h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            {pageSettings.cta_subtitle}
          </p>
          <Button 
            variant="default" 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-4 rounded-xl shadow-lg shadow-blue-600/20"
            onClick={() => navigate('/seller-signup')}
          >
            Create Your Seller Account
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SellersToolbox;
